import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { useAuth } from "./useAuth";
import { useOrderOnBehalf } from "./useOrderOnBehalf";

interface CartContextType {
  cartCount: number;
  supplementalCount: number;
  refreshCart: () => Promise<void>;
  addToCart: (productId: number, quantity?: number) => Promise<{ success: boolean; warning?: string; error?: string }>;
  clearCart: () => Promise<void>;
}

const CartContext = createContext<CartContextType>({
  cartCount: 0,
  supplementalCount: 0,
  refreshCart: async () => {},
  addToCart: async () => ({ success: false }),
  clearCart: async () => {},
});

export function CartProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { customer: onBehalfCustomer } = useOrderOnBehalf();
  const [cartCount, setCartCount] = useState(0);
  const [supplementalCount, setSupplementalCount] = useState(0);

  const forCustomerId = onBehalfCustomer?.id ?? null;

  const buildUrl = useCallback((base: string) => {
    if (forCustomerId) {
      const sep = base.includes("?") ? "&" : "?";
      return `${base}${sep}forCustomerId=${forCustomerId}`;
    }
    return base;
  }, [forCustomerId]);

  const refreshCart = useCallback(async () => {
    if (!user) {
      setCartCount(0);
      setSupplementalCount(0);
      return;
    }
    try {
      const supplementalUrl = forCustomerId
        ? `/api/cart/customer/${forCustomerId}`
        : user.role !== "admin"
          ? "/api/cart/on-behalf-items"
          : null;

      const [mainRes, suppRes] = await Promise.all([
        fetch(buildUrl("/api/cart"), { credentials: "include" }),
        supplementalUrl ? fetch(supplementalUrl, { credentials: "include" }).catch(() => null) : Promise.resolve(null),
      ]);

      if (!mainRes.ok) {
        setCartCount(0);
        setSupplementalCount(0);
        return;
      }
      const mainData = await mainRes.json().catch(() => []);
      const mainItems = Array.isArray(mainData) ? mainData : [];
      setCartCount(mainItems.reduce((sum: number, item: any) => sum + item.quantity, 0));

      if (suppRes && suppRes.ok) {
        const suppItems = await suppRes.json().catch(() => []);
        setSupplementalCount(Array.isArray(suppItems) ? suppItems.reduce((sum: number, item: any) => sum + item.quantity, 0) : 0);
      } else {
        setSupplementalCount(0);
      }
    } catch {
      setCartCount(0);
      setSupplementalCount(0);
    }
  }, [user, buildUrl, forCustomerId]);

  useEffect(() => {
    refreshCart();
  }, [refreshCart]);

  const addToCart = useCallback(async (productId: number, quantity: number = 1): Promise<{ success: boolean; error?: string }> => {
    try {
      const res = await fetch(buildUrl("/api/cart"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ productId, quantity, forCustomerId }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        return { success: false, error: data.error || "Failed to add to cart" };
      }
      const data = await res.json().catch(() => ({}));
      await refreshCart();
      return { success: true, warning: data.warning };
    } catch {
      return { success: false, error: "Failed to add to cart" };
    }
  }, [refreshCart, buildUrl, forCustomerId]);

  const clearCart = useCallback(async () => {
    try {
      const res = await fetch(buildUrl("/api/cart"), { method: "DELETE", credentials: "include" });
      if (res.ok) {
        setCartCount(0);
      }
    } catch {}
  }, [buildUrl]);

  return (
    <CartContext.Provider value={{ cartCount, supplementalCount, refreshCart, addToCart, clearCart }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  return useContext(CartContext);
}
